import React, { useState, useEffect, useRef } from 'react';
import {Box, Container, Flex, HStack, Icon, Image, Link, SimpleGrid, Stack, Text, VStack} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaDiscord, FaTwitter } from 'react-icons/fa';
import logoImage from '../../media/Logo.png';
import weChatImage from '../../media/wechat.jpg';
import redNoteImage from '../../media/xiaohongshu.png';
import {Link as RouterLink} from "react-router-dom";

const Footer = () => {
	const { t } = useTranslation();
	const [enlargedImage, setEnlargedImage] = useState<'rednote' | 'wechat' | null>(null);
	const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
	const enlargedImageRef = useRef<HTMLDivElement>(null);
	const redNoteImageRef = useRef<HTMLImageElement>(null);
	const weChatImageRef = useRef<HTMLImageElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (enlargedImageRef.current && !enlargedImageRef.current.contains(event.target as Node)) {
				setEnlargedImage(null);
			}
		};

		if (enlargedImage) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [enlargedImage]);

	const handleImageClick = (imageType: 'rednote' | 'wechat') => {
		const rect = imageType === 'rednote'
			? redNoteImageRef.current?.getBoundingClientRect()
			: weChatImageRef.current?.getBoundingClientRect();
		if (rect) {
			const popupWidth = 210;
			const popupHeight = 210;
			const padding = 8;
			let x = rect.left + rect.width / 2 - popupWidth / 2;
			let y = rect.top - popupHeight - 10;

			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			if (x < padding) {
				x = padding;
			} else if (x + popupWidth > viewportWidth - padding) {
				x = viewportWidth - popupWidth - padding;
			}

			if (y < padding) {
				y = rect.bottom + 10;
			}

			setPopupPosition({ x, y });
		}
		setEnlargedImage(imageType);
	};

	return (
        <Box
            as="footer"
            bg="surfaceDark"
            color="onDarkSoft"
            py={{ base: 12, md: 16 }}
        >
            <Container maxW="1200px" px={{ base: 4, md: 8 }}>
                <Stack spacing={10}>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={{ base: 8, md: 10 }}>
                        <VStack align="start" spacing={4}>
                            <HStack spacing={3}>
                                <Image src={logoImage} alt="TCF CANADA" height={'36px'} objectFit="contain" />
                                <Text
                                    color="onDark"
                                    fontSize="xl"
                                    fontWeight={500}
                                    letterSpacing={'-0.3px'}
                                    lineHeight={1.2}
                                >
                                    Master Key
                                </Text>
                            </HStack>
                            <Text fontSize="sm" color="onDarkSoft" lineHeight={1.6}>
                                {t('footer.platform_intro')}
                            </Text>
                        </VStack>

                        <VStack align="start" spacing={3}>
                            <Text
                                fontSize="sm"
                                fontWeight={600}
                                color="onDark"
                                textTransform="uppercase"
                                letterSpacing="1.5px"
                            >
                                {t('footer.contact_us')}
                            </Text>
                            <Link
                                href="mailto:tcfcanada@tcfkey.com"
                                fontSize="sm"
                                color="onDarkSoft"
                                _hover={{ color: 'brand.primary' }}
                                fontWeight={500}
                            >
                                tcfcanada@tcfkey.com
                            </Link>
                        </VStack>

                        <VStack align="start" spacing={3}>
                            <Text
                                fontSize="sm"
                                fontWeight={600}
                                color="onDark"
                                textTransform="uppercase"
                                letterSpacing="1.5px"
                            >
                                {t('footer.social_media')}
                            </Text>
                            <Flex gap={3}>
                                <Link
                                    href="https://discord.gg/dj2jNkFDeB"
                                    isExternal
                                    aria-label="Discord"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    w="36px"
                                    h="36px"
                                    borderRadius="md"
                                    bg="surfaceDarkElevated"
                                    color="onDarkSoft"
                                    _hover={{ bg: 'brand.primary', color: 'onPrimary' }}
                                    transition="all 0.2s ease"
                                >
                                    <Icon as={FaDiscord} boxSize={5} />
                                </Link>
                                <Link
                                    href="https://x.com/MTcfca40438"
                                    isExternal
                                    aria-label="x.com"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    w="36px"
                                    h="36px"
                                    borderRadius="md"
                                    bg="surfaceDarkElevated"
                                    color="onDarkSoft"
                                    _hover={{ bg: 'brand.primary', color: 'onPrimary' }}
                                    transition="all 0.2s ease"
                                >
                                    <Icon as={FaTwitter} boxSize={5} />
                                </Link>
                            </Flex>
                        </VStack>

                        <VStack align="start" spacing={3}>
                            <Text
                                fontSize="sm"
                                fontWeight={600}
                                color="onDark"
                                textTransform="uppercase"
                                letterSpacing="1.5px"
                            >
                                {t('footer.follow_us')}
                            </Text>
                            <Flex gap={4}>
                                <VStack spacing={2}>
                                    <Image
                                        ref={redNoteImageRef}
                                        src={redNoteImage}
                                        alt="Red Note"
                                        boxSize="64px"
                                        borderRadius="lg"
                                        objectFit="cover"
                                        border="1px solid"
                                        borderColor="surfaceDarkElevated"
                                        cursor="pointer"
                                        onClick={() => handleImageClick('rednote')}
                                        transition="transform 0.2s ease"
                                        _hover={{ transform: 'scale(1.05)' }}
                                    />
                                    <Text fontSize="xs" color="onDarkSoft">{t('footer.red_note')}</Text>
                                </VStack>
                                <VStack spacing={2}>
                                    <Image
                                        ref={weChatImageRef}
                                        src={weChatImage}
                                        alt="WeChat"
                                        boxSize="64px"
                                        borderRadius="lg"
                                        objectFit="cover"
                                        border="1px solid"
                                        borderColor="surfaceDarkElevated"
                                        cursor="pointer"
                                        onClick={() => handleImageClick('wechat')}
                                        transition="transform 0.2s ease"
                                        _hover={{ transform: 'scale(1.05)' }}
                                    />
                                    <Text fontSize="xs" color="onDarkSoft">{t('footer.wechat_label')}</Text>
                                </VStack>
                            </Flex>
                        </VStack>
                    </SimpleGrid>

                    <Box
                        borderTop="1px solid"
                        borderColor="surfaceDarkElevated"
                        pt={6}
                    >
                        <Flex
                            direction={{ base: 'column', md: 'row' }}
                            justify="space-between"
                            align="center"
                            gap={4}
                        >
                            <Text fontSize="sm" color="onDarkSoft">
                                © {new Date().getFullYear()} TCF Canada Master Key. {t('footer.all_rights_reserved')}
                            </Text>
                            <HStack spacing={6}>
                                <Link
                                    as={RouterLink}
                                    to="/"
                                    fontSize="sm"
                                    color="onDarkSoft"
                                    _hover={{ color: 'brand.primary' }}
                                    fontWeight={500}
                                >
                                    {t('header.homepage')}
                                </Link>
                                <Link
                                    as={RouterLink}
                                    to="/subscription"
                                    fontSize="sm"
                                    color="onDarkSoft"
                                    _hover={{ color: 'brand.primary' }}
                                    fontWeight={500}
                                >
                                    {t('header.Subscription')}
                                </Link>
                                <Link
                                    as={RouterLink}
                                    to="/login"
                                    fontSize="sm"
                                    color="onDarkSoft"
                                    _hover={{ color: 'brand.primary' }}
                                    fontWeight={500}
                                >
                                    {t('login.login')}
                                </Link>
                            </HStack>
                        </Flex>
                    </Box>
                </Stack>
            </Container>
            {enlargedImage && (
                <Box
                    ref={enlargedImageRef}
                    position="fixed"
                    left={`${popupPosition.x}px`}
                    top={`${popupPosition.y}px`}
                    zIndex={9999}
                    bg="surfaceDarkElevated"
                    borderRadius="lg"
                    p={2}
                    border="1px solid"
                    borderColor="surfaceDarkElevated"
                    boxShadow="0 4px 20px rgba(0,0,0,0.5)"
                >
                    <Image
                        src={enlargedImage === 'rednote' ? redNoteImage : weChatImage}
                        alt={enlargedImage === 'rednote' ? 'Red Note' : 'WeChat'}
                        width="210px"
                        height="210px"
                        objectFit="cover"
                        borderRadius="md"
                    />
                </Box>
            )}
        </Box>
	);
};

export default Footer;
